import { useState } from 'react';
import { Mail, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/** Coach-side form: emails a parent a signup link with the team code + child name pre-filled. */
export function ParentInviteForm({ teamId }: { teamId: string }) {
  const [email, setEmail] = useState('');
  const [childName, setChildName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const em = email.trim();
    const cn = childName.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) { setError('Enter a valid parent email address.'); return; }
    if (!cn) { setError("Enter the child's name."); return; }
    setBusy(true); setError('');
    const { data, error: fnError } = await supabase.functions.invoke('send-parent-invite', {
      body: { team_id: teamId, parent_email: em, child_name: cn },
    });
    setBusy(false);
    if (fnError || (data as any)?.error) {
      setError((data as any)?.error || 'Could not send the invite. Please try again.');
      return;
    }
    toast.success(`Invite sent to ${em}`);
    setSentTo(`${cn} (${em})`);
    setEmail(''); setChildName('');
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-border bg-card/60 p-4 space-y-3" aria-label="Invite a parent">
      <div>
        <h4 className="font-display font-bold text-sm text-foreground flex items-center gap-2">
          <Mail className="w-4 h-4 text-primary" /> Invite a parent by email
        </h4>
        <p className="text-xs text-muted-foreground mt-1">
          We'll email a signup link with your team code and the child's name filled in. Once the parent adds their child, they appear in your roster.
        </p>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <label htmlFor="invite-child" className="sr-only">Child's name</label>
          <Input id="invite-child" value={childName} maxLength={40} onChange={e => { setChildName(e.target.value); setError(''); }} placeholder="Child's first name" disabled={busy} />
        </div>
        <div>
          <label htmlFor="invite-email" className="sr-only">Parent email</label>
          <Input id="invite-email" type="email" value={email} maxLength={254} onChange={e => { setEmail(e.target.value); setError(''); }} placeholder="parent@email.com" disabled={busy} />
        </div>
      </div>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {sentTo && !error && <p role="status" className="text-xs text-primary flex items-center gap-1"><Check className="w-3 h-3" /> Invite sent for {sentTo}</p>}
      <Button type="submit" size="sm" disabled={busy} className="font-display font-bold">
        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
        {busy ? 'Sending…' : 'Send invite'}
      </Button>
    </form>
  );
}
